import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { AuthService } from '../../../services/auth/auth.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { PushNotificationsService } from '../../../services/push-notifications/push-notifications.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent {
  loginForm: FormGroup;
  showPassword: boolean = false;
  isLoading: boolean = false;

  ipDetails: any;
  loginDetails: any;
  accessDetails: any;
  subscriptionDetails: any;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private authService: AuthService,
    private userAccountService: UserAccountService,
    private pushNotificationsService: PushNotificationsService,
  ) {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
      remember: [false],
    });
  }

  onSubmit() {
    this.getIpDetails();
  }

  login() {
    this.isLoading = true;
    this.loginForm.disable();

    this.authService.login(this.getLoginPayload()).subscribe({
      next: (response) => {
        // console.log('LOGIN ENDPOINT RESPONSE: ', response);
        if (response.data.requires_2fa) {
          localStorage.setItem('email', this.loginForm.get('username')?.value);
          this.router.navigate(['/login/verify-2fa']);
        } else {
          this.isLoading = false;
          if (response.status === 'Operation successful') {
            this.toastr.success('Signin successful');
            this.loginDetails = response?.data;
            this.storeLoginDetails();
            this.getAccessPermissions();

            if (response.data.enable_ip_tracking) {
              this.updateIpDetails();
            }
          } else {
            console.log(response.status);
          }
        }
      },
      error: (error) => {
        console.error(error);

        this.toastr.error(error.error?.message_key || 'Something went wrong');

        this.loginForm.enable();
        this.loginForm.reset();
      },
      complete: () => {
        this.loginForm.enable();
        this.loginForm.reset();
      },
    });
  }

  getAccessPermissions() {
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        // console.log('GET USER PROFILE RESPONSE: ', response);

        const subscription = response?.data?.subscriptions?.[0] || {};

        this.subscriptionDetails = subscription;
        this.storeSubscriptionDetails();

        this.accessDetails = {
          face_search_permission: subscription?.face_search_permission,
          face_compare_permission: subscription?.face_compare_permission,
          video_scanner_permission: subscription?.video_scanner_permission,
          osint_search_permission: subscription?.osint_search_permission,
          leak_search_permission: subscription?.leak_search_permission,
          pan_search_permission: subscription?.pan_search_permission,
          gst_search_permission: subscription?.gst_search_permission,
          din_search_permission: subscription?.din_search_permission,
          mobile_pan_search_permission: subscription?.mobile_pan_search_permission,
          phonepe_fastag_search_permission: subscription?.phonepe_fastag_search_permission,
          phonepe_upi_search_permission: subscription?.phonepe_upi_search_permission,
          vehicle_search_permission: subscription?.vehicle_search_permission,
        };
        this.storeAccessDetails();

        // Ask for browser notification permission right after login.
        this.pushNotificationsService.requestPushAfterLogin();

        if (subscription?.osint_search_permission) {
          this.router.navigate(['/osint-number-search']);
        } else if (subscription?.face_search_permission) {
          this.router.navigate(['/osint-image-search']);
        } else if (subscription?.face_compare_permission) {
          this.router.navigate(['/osint-image-search/face-compare']);
        } else if (subscription?.video_scanner_permission) {
          this.router.navigate(['/osint-image-search/video-scanner']);
        } else if (subscription?.leak_search_permission) {
          this.router.navigate(['/leak-number-search']);
        } else {
          this.router.navigate(['/user-account']);
        }
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {},
    });
  }

  getTrackingPayload() {
    const locationParts = [
      this.ipDetails?.city,
      this.ipDetails?.region,
      this.ipDetails?.country,
    ];
    const location = locationParts.filter(Boolean).join(', ');
    return {
      location: location || undefined,
      ip_address: this.ipDetails?.ip,
      device: this.getDeviceDetails(),
    };
  }

  getLoginPayload() {
    return {
      login: this.loginForm.get('username')?.value,
      password: this.loginForm.get('password')?.value,
      // location:
      //   this.ipDetails.city +
      //   ', ' +
      //   this.ipDetails.region +
      //   ', ' +
      //   this.ipDetails.country,
      // ip_address: this.ipDetails.ip,
      // device: this.getDeviceDetails(),
    };
  }

  updateIpDetails() {
    this.authService.getIpDetailsWithFallback().subscribe({
      next: (response) => {
        this.ipDetails = response;
        this.storeIpDetails();
        this.userAccountService
          .sendTrackingInfo(this.getTrackingPayload())
          .subscribe((data) => {});
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  getIpDetails() {
    if (this.loginForm.valid) {
      // this.authService.getIpDetails().subscribe({
      //   next: (response) => {
      // console.log('IP DETAILS: ', response);

      //this.ipDetails = response;
      this.storeIpDetails();
      this.login();
      //   },
      //   error: (error) => {
      //     console.error(error);
      //   },
      // });
    } else {
      this.loginForm.markAllAsTouched();
      this.isLoading = false;
    }
  }

  storeIpDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : {};

    userPortal.ipDetails = this.ipDetails;
    localStorage.setItem('USER_PORTAL', JSON.stringify(userPortal));
  }

  storeLoginDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : {};

    userPortal.loginDetails = this.loginDetails;
    localStorage.setItem('USER_PORTAL', JSON.stringify(userPortal));
  }

  storeAccessDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : {};

    userPortal.accessDetails = this.accessDetails;
    localStorage.setItem('USER_PORTAL', JSON.stringify(userPortal));
  }

  storeSubscriptionDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : {};

    userPortal.subscriptionDetails = this.subscriptionDetails;
    localStorage.setItem('USER_PORTAL', JSON.stringify(userPortal));
  }

  getDeviceDetails() {
    return navigator.userAgent;
  }
}
